// src/stores/useProjectStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { PERSIST_KEYS } from "../auth/persistKeys";

const GLOBAL_PROJECT_ID = "global";

function normalizeProjectId(id) {
  if (!id) return GLOBAL_PROJECT_ID;
  const s = String(id).trim();
  return s.length ? s : GLOBAL_PROJECT_ID;
}

export const useProjectStore = create(
  persist(
    (set, get) => ({

      /* -------------------------------------------------- */
      /* STATE                                               */
      /* -------------------------------------------------- */

      projects: [],

      // Canonical identity (UUID or "global")
      activeProjectId: GLOBAL_PROJECT_ID,
      activeProject: null,
      role: null,
      isGlobal: true,
      hasHydrated: false,
      bbox: null,
      isLoaded: false,

      /* -------------------------------------------------- */
      /* SELECTORS                                           */
      /* -------------------------------------------------- */

      getResolvedProject: () => {
        const { projects, activeProjectId } = get();
        if (!activeProjectId || activeProjectId === GLOBAL_PROJECT_ID) {
          return null;
        }
        return projects.find(p => p.id === activeProjectId) || null;
      },

      requireProject: () => {
        const { isGlobal, activeProjectId } = get();
        return !isGlobal && !!activeProjectId;
      },

      /* -------------------------------------------------- */
      /* LOAD PROJECTS                                       */
      /* -------------------------------------------------- */

      loadProjects: async () => {
        try {
          const res = await fetchWithAuth("/api/v1/projects/available");
          if (!res.ok) {
            throw new Error(`Failed to load projects: ${res.status}`);
          }

          const owned = await res.json();
          let projects = Array.isArray(owned) ? owned : [];

          // Merge in PadRekko's participant-scoped project list (owned ∪
          // has-an-accessible-case). This is what lets a customer/operator
          // tenant see — and filter cases by — the DH-owned project behind a
          // shared case, WITHOUT touching /projects/available's owner-org
          // contract. Union by id; the owner-scoped row wins on collision so
          // its richer metadata (bbox, member role) is preserved. Non-PadRekko
          // users get a 403 here; we swallow it and fall back to `owned`.
          try {
            const qcRes = await fetchWithAuth("/api/v1/padrekko/qc/projects");
            if (qcRes.ok) {
              const body = await qcRes.json();
              const qcProjects = Array.isArray(body?.projects) ? body.projects : [];
              const seen = new Set(projects.map(p => p.id));
              for (const p of qcProjects) {
                if (!seen.has(p.id)) {
                  projects.push(p);
                  seen.add(p.id);
                }
              }
            }
          } catch (err) {
            // Non-fatal — picker just falls back to the owner-scoped list.
            console.warn("[projects] padrekko project merge skipped", err);
          }

          set({ projects });

          // -------- Warm Start Resolution --------
          const { activeProjectId } = get();

          if (activeProjectId && activeProjectId !== GLOBAL_PROJECT_ID) {
            const match = projects.find(p => p.id === activeProjectId);

            if (match) {
              set({
                activeProject: match,
                role: match.role ?? null,
                isGlobal: false,
              });
            }
          }

        } catch (err) {
          console.error("[projects] load failed", err);
        }
      },

      /* -------------------------------------------------- */
      /* SETTERS                                             */
      /* -------------------------------------------------- */

      setProjects: (projects) => {
        const safe = Array.isArray(projects) ? projects : [];
        set({ projects: safe });
      },

      setActiveProject: (project) => {
        if (!project?.id) {
          set({
            activeProjectId: GLOBAL_PROJECT_ID, activeProject: null,
            role: null, isGlobal: true, bbox: null, isLoaded: false
          });
          return;
        }
        set({
          activeProjectId: project.id,
          activeProject: project,
          role: project.role ?? null,
          isGlobal: false,
          bbox: project.bbox ?? null,
          isLoaded: true,
        });
      },

      setActiveProjectId: (id) => {
        const next = normalizeProjectId(id);

        if (next === GLOBAL_PROJECT_ID) {
          set({
            activeProjectId: GLOBAL_PROJECT_ID,
            activeProject: null,
            role: null,
            isGlobal: true,
          });
          return;
        }

        const { projects } = get();
        const match = projects.find(p => p.id === next);

        set({
          activeProjectId: next,
          activeProject: match ?? null,
          role: match?.role ?? null,
          isGlobal: false,
        });
      },

      resetToGlobal: () =>
        set({
          activeProjectId: GLOBAL_PROJECT_ID,
          activeProject: null,
          role: null,
          isGlobal: true,
        }),

      /**
       * Wipe all user-scoped state. Registered with userBoundary so an
       * in-tab user-switch can't leak project_id into another user's
       * tile-session URL (the bug that surfaced earlier when a demo
       * operator's session inherited a Dreamhammer project_id).
       *
       * persist() only persists activeProjectId, so the on-disk wipe is
       * already handled by enforceSessionBoundary on different-user
       * page-loads. This handles the same-tab case.
       */
      _clearUserScoped: () =>
        set({
          projects: [],
          activeProjectId: GLOBAL_PROJECT_ID,
          activeProject: null,
          role: null,
          isGlobal: true,
          bbox: null,
          isLoaded: false,
        }),

    }),
    {
      name: PERSIST_KEYS.project,
      version: 3,
      partialize: (state) => ({
        activeProjectId: state.activeProjectId,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hasHydrated = true;
          state.isGlobal = state.activeProjectId === GLOBAL_PROJECT_ID;
        }
      },
    }
  )
);

export const PROJECT_GLOBAL_ID = GLOBAL_PROJECT_ID;

// Register the user-boundary reset. Logout-then-login still goes
// through the KC redirect → full page reload path, where the persist
// localStorage key is wiped by enforceSessionBoundary. This catches
// the same-tab user-switch case.
import { registerUserBoundaryReset } from "../auth/userBoundary";
registerUserBoundaryReset(
  () => useProjectStore.getState()._clearUserScoped(),
  () => useProjectStore.getState().loadProjects(),
);